import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Mail, 
  Send, 
  AlertTriangle, 
  Check, 
  FileText, 
  Copy, 
  Trash
} from 'lucide-react';

const EmailDialog = ({ 
  emailDialogOpen, 
  setEmailDialogOpen, 
  selectedUser, 
  emailData, 
  setEmailData, 
  handleSendEmail 
}) => {
  // Estado local para controlar la vista previa y plantillas
  const [viewPreview, setViewPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Lista de plantillas predefinidas
  const emailTemplates = [
    {
      id: 'verification',
      title: 'Verificación de cuenta',
      subject: 'Por favor verifica tu cuenta - InKayros.com',
      body: `Estimado/a ${selectedUser?.nombre || 'usuario'},

Hemos detectado que aún no has verificado tu cuenta en InKayros.com.

Para acceder a todas las funcionalidades de nuestra plataforma, te pedimos que completes el proceso de verificación haciendo clic en el enlace de verificación que te enviamos cuando te registraste.

Si no encuentras ese correo, puedes solicitar uno nuevo en la sección de perfil de la plataforma.

Gracias por formar parte de nuestra comunidad.`
    },
    {
      id: 'suspension',
      title: 'Notificación de suspensión',
      subject: 'Información importante sobre tu cuenta - InKayros.com',
      body: `Estimado/a ${selectedUser?.nombre || 'usuario'},

Lamentamos informarte que tu cuenta ha sido temporalmente suspendida debido a posibles infracciones a nuestros términos y condiciones.

Para reactivar tu cuenta, por favor comunícate con nuestro equipo de soporte respondiendo a este correo con la información relevante.

Agradecemos tu comprensión.`
    },
    {
      id: 'payment',
      title: 'Problemas de pago',
      subject: 'Actualización de información de pago requerida - InKayros.com',
      body: `Estimado/a ${selectedUser?.nombre || 'usuario'},

Hemos detectado un problema con tu método de pago actual. Para evitar la interrupción de tu servicio, te pedimos que actualices tu información de pago lo antes posible.

Puedes hacerlo accediendo a la sección de facturación en tu perfil.

Si necesitas ayuda, no dudes en contactar a nuestro equipo de soporte.`
    }
  ];
  
  // Aplicar una plantilla
  const applyTemplate = (template) => {
    setEmailData({
      subject: template.subject,
      message: template.body
    });
    setShowTemplates(false);
  };
  
  // Enviar el email
  const sendEmail = async () => {
    setSending(true);
    try {
      await handleSendEmail();
      setSuccessMessage('Email enviado exitosamente');
      // No cerramos el diálogo automáticamente para mostrar el mensaje de éxito
      setTimeout(() => {
        setSuccessMessage('');
        setEmailDialogOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Error al enviar email:', error);
    } finally {
      setSending(false);
    }
  };
  
  // Limpiar los campos
  const clearFields = () => {
    setEmailData({ subject: '', message: '' });
  };
  
  // Verificar si los campos están completos
  const isFormValid = emailData.subject.trim() !== '' && emailData.message.trim() !== '';

  return (
    <AnimatePresence>
      {emailDialogOpen && (
        <motion.div
          className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEmailDialogOpen(false)} />
          
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-screen overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="px-6 py-4 bg-indigo-600 text-white flex justify-between items-center">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {viewPreview ? 'Vista Previa del Email' : 'Enviar Email'}
              </h3>
              <button 
                onClick={() => setEmailDialogOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Información del destinatario */}
              <div className="bg-indigo-50 p-4 rounded-lg mb-5">
                <h4 className="text-sm font-semibold text-indigo-800 mb-2">Destinatario:</h4>
                <div className="flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">
                    {selectedUser?.nombre || 'Usuario'} &lt;{selectedUser?.email || 'email@ejemplo.com'}&gt;
                  </span>
                </div>
              </div>
              
              {/* Pestañas: Editar / Vista Previa */}
              <div className="border-b border-gray-200 mb-4">
                <div className="flex gap-4">
                  <button
                    onClick={() => setViewPreview(false)}
                    className={`pb-2 px-1 text-sm font-medium transition-colors ${
                      !viewPreview 
                        ? 'text-indigo-600 border-b-2 border-indigo-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => setViewPreview(true)}
                    className={`pb-2 px-1 text-sm font-medium transition-colors ${
                      viewPreview 
                        ? 'text-indigo-600 border-b-2 border-indigo-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Vista Previa
                  </button>
                </div>
              </div>
              
              {/* Mensajes de éxito/error */}
              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  {successMessage}
                </div>
              )}
              
              {viewPreview ? (
                /* Vista previa del email */
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <div className="mb-4 pb-3 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">{emailData.subject || '(Sin asunto)'}</h2>
                  </div>
                  <div className="whitespace-pre-line text-gray-700">
                    {emailData.message || '(Sin contenido)'}
                  </div>
                </div>
              ) : (
                /* Formulario de edición */
                <>
                  {/* Botón de plantillas */}
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => setShowTemplates(!showTemplates)}
                      className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <FileText className="w-4 h-4" />
                      {showTemplates ? 'Ocultar plantillas' : 'Usar plantilla'}
                    </button>
                  </div>
                  
                  {/* Panel de plantillas */}
                  <AnimatePresence>
                    {showTemplates && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-4 overflow-hidden"
                      >
                        <div className="bg-indigo-50 p-4 rounded-lg">
                          <h4 className="text-sm font-semibold text-indigo-800 mb-2">Plantillas disponibles:</h4>
                          <div className="space-y-2">
                            {emailTemplates.map(template => (
                              <div 
                                key={template.id}
                                className="bg-white p-3 rounded border border-indigo-100 hover:border-indigo-300 cursor-pointer transition-colors"
                                onClick={() => applyTemplate(template)}
                              >
                                <div className="font-medium text-indigo-700">{template.title}</div>
                                <div className="text-xs text-gray-500 truncate">{template.subject}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Campos del formulario */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Asunto
                      </label>
                      <input
                        id="email-subject"
                        type="text"
                        value={emailData.subject}
                        onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                        placeholder="Escribe el asunto del email"
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 
                                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email-message" className="block text-sm font-medium text-gray-700 mb-1">
                        Mensaje
                      </label>
                      <textarea
                        id="email-message"
                        value={emailData.message}
                        onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                        placeholder="Escribe el mensaje que deseas enviar"
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 
                                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        rows={10}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <div>
                {!viewPreview && (
                  <motion.button
                    onClick={clearFields}
                    className="px-3 py-2 text-gray-600 flex items-center gap-1 hover:text-gray-800"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={sending}
                  >
                    <Trash className="w-4 h-4" />
                    Limpiar
                  </motion.button>
                )}
              </div>
              
              <div className="flex gap-3">
                <motion.button
                  onClick={() => setEmailDialogOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={sending}
                >
                  Cancelar
                </motion.button>
                
                {viewPreview ? (
                  <motion.button
                    onClick={() => setViewPreview(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Volver a Editar
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={sendEmail}
                    className={`px-4 py-2 flex items-center gap-2 rounded-lg shadow-md
                                ${isFormValid ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    whileHover={isFormValid ? { scale: 1.02 } : {}}
                    whileTap={isFormValid ? { scale: 0.98 } : {}}
                    disabled={!isFormValid || sending}
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar Email
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailDialog;